import React, { useEffect, useState } from 'react'

import { Autocomplete, Box, Checkbox, Chip, FormControl, ListItemText, MenuItem, Modal, Select, TextField, Tooltip } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import Swal from 'sweetalert2'
import { useNavigate } from 'react-router-dom'

import AppLayout from '../../components/Layouts/AppLayout'
import Loading from '../../components/Loading'
import axios from '../../lib/axios'
import { useStateContext } from '../../context/ContextProvider'
import { isValid, handleAxiosError } from '../../helpers/helper'
import { TEXTFIELD_STYLE } from '../../constants/constants'
import forecastCalculationBasisAPI from '../../services/api/Forecasts/forecastCalculationBasisAPI'


const modalStyle = {
    minWidth: "360px",
    maxWidth: "900px",
    width: "60%",
    maxHeight: '85vh',
    overflowY: 'auto',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: '#ffffff',
    border: 'transparent',
    borderRadius: '5px',
    boxShadow: 24,
    outline: 'none'
}

const detailsModalStyle = {
    ...modalStyle,
    width: 'fit-content',
    maxWidth: '95vw'
}

const SupplyMethodChip = ({ method, t }) => (
    <Chip
        label={method === 'buy' ? t('buy') : t('build')}
        size='small'
        sx={{
            backgroundColor: method === 'buy' ? '#FFF3E3' : '#ECF0EC',
            borderRadius: '8px',
            height: '24px',
            padding: '4px 12px',
            color: method === 'buy' ? 'rgba(154, 108, 34, 0.87)' : 'rgba(36, 75, 40, 0.87)',
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 400,
            fontSize: '14px',
            letterSpacing: '0.15px',
            '& .MuiChip-label': { padding: 0 }
        }}
    />
)

const EmptyState = ({ icon, title, description }) => (
    <div className='flex flex-col items-center justify-center text-center p-10'>
        <div className='w-14 h-14 rounded-full bg-[#F5F5F5] flex items-center justify-center mb-4'>
            <i className={`fa-solid ${icon} text-[#A1ACB8] text-[20px]`}></i>
        </div>
        <p className='text-[15px] font-[600] text-[#333333]'>{title}</p>
        <p className='text-[13px] text-[#88909C] pt-1'>{description}</p>
    </div>
)

const KpiCard = ({ icon, label, value }) => (
    <div className='w-full bg-white flex items-center' style={{ borderRadius: '4px', border: '0.8px solid rgba(0,0,0,0.12)', padding: '16px', gap: '12px' }}>
        <div className='w-10 h-10 rounded-full bg-[#F5F5F5] flex items-center justify-center flex-shrink-0'>
            <i className={`fa-solid ${icon} text-black text-[16px]`}></i>
        </div>
        <div>
            <p className='text-[13px] font-[500] text-[#718096]'>{label}</p>
            <p className='text-[20px] font-[700] text-[#333333] pt-0.5'>{value}</p>
        </div>
    </div>
)

const DetailField = ({ label, value, highlight = false }) => (
    <div
        className='flex justify-between items-center'
        style={{ padding: '15px 10px', minHeight: highlight ? '46px' : 'auto', backgroundColor: highlight ? '#F4F8FC' : 'transparent' }}
    >
        <span style={{ fontFamily: 'Roboto, sans-serif', fontWeight: highlight ? 600 : 400, fontSize: '14px', lineHeight: '100%', letterSpacing: '0.15px', color: highlight ? '#336195' : '#88909C' }}>
            {label}
        </span>
        <div style={{ fontFamily: 'Roboto, sans-serif', fontWeight: highlight ? 600 : 400, fontSize: '14px', lineHeight: '100%', letterSpacing: '0.15px', color: highlight ? '#336195' : '#171717', textAlign: 'right', maxWidth: '180px' }}>
            {value}
        </div>
    </div>
)

const Mrp = () => {
    const { t } = useTranslation()
    const { company_id, choosesite, config, setCompany_id } = useStateContext()
    const navigate = useNavigate()

    const [isLoading, setIsLoading] = useState(false)
    const [sites, setSites] = useState([])
    const [calculationBasisOptions, setCalculationBasisOptions] = useState([])
    const [palletTypeOptions, setPalletTypeOptions] = useState([])
    const [items, setItems] = useState([])
    const [mrpResult, setMrpResult] = useState(null)
    const [lastRunAt, setLastRunAt] = useState(null)

    const [productionDetailsOpen, setProductionDetailsOpen] = useState(false)
    const [materialDetailsOpen, setMaterialDetailsOpen] = useState(false)
    const [selectedRow, setSelectedRow] = useState(null)
    const [productionPageSize, setProductionPageSize] = useState(10)
    const [materialPageSize, setMaterialPageSize] = useState(10)

    /* form */
    const [siteId, setSiteId] = useState('')
    const [siteName, setSiteName] = useState('')
    const [periodFrom, setPeriodFrom] = useState(dayjs())
    const [periodTo, setPeriodTo] = useState(dayjs())
    const [calculationBasis, setCalculationBasis] = useState('')
    const [palletTypes, setPalletTypes] = useState([])
    const [selectedItems, setSelectedItems] = useState([])

    useEffect(() => {
        if (!isValid(company_id)) {
            setCompany_id(localStorage.getItem('company_id'))
        }
    }, [])

    useEffect(() => {
        if (isValid(company_id)) {
            getSites(company_id)
            getItems(company_id)
        }
    }, [company_id])

    useEffect(() => {
        getCalculationBasisOptions()
    }, [])

    useEffect(() => {
        getPalletTypes()
    }, [])

    useEffect(() => {
        if (!isValid(siteId) && sites.length > 0) {
            const defaultSiteId = choosesite || localStorage.getItem('site')
            const defaultSite = sites.find(site => String(site.id) === String(defaultSiteId))

            if (defaultSite) {
                setSiteId(defaultSite.id)
                setSiteName(defaultSite.name)
            }
        }
    }, [sites, choosesite])

    const getSites = async (id) => {
        await axios
            .get(`/api/sites?company_id=${id}`, config)
            .then(res => {
                setSites(res.data)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const handleSiteChange = (a, b) => {
        setSiteId(b?.id || '')
        setSiteName(b?.label || '')
    }

    const getCalculationBasisOptions = async () => {
        await forecastCalculationBasisAPI.getForecastCalculationBasis(config)
            .then((data) => {
                setCalculationBasisOptions(data)
            })
    }

    const getPalletTypes = async () => {
        await axios
            .get('/api/pallet-types', config)
            .then(res => {
                setPalletTypeOptions(Array.isArray(res.data) ? res.data : [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getItems = async (id) => {
        await axios
            .get(`/api/items?company_id=${id}`, config)
            .then(res => {
                setItems(res.data ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const handlePalletTypesChange = (e) => {
        const value = e.target.value
        const realValues = palletTypeOptions.map((option) => option.id)
        const isAllCurrentlySelected = realValues.length > 0 && palletTypes.length === realValues.length

        if (value.includes('all')) {
            setPalletTypes(isAllCurrentlySelected ? [] : realValues)
        } else {
            setPalletTypes(value)
        }
    }

    const handleItemsChange = (a, values) => {
        const clickedAll = (values ?? []).some((value) => value.id === 'all')

        if (clickedAll) {
            const isAllCurrentlySelected = optionsItems.length > 0 && selectedItems.length === optionsItems.length
            setSelectedItems(isAllCurrentlySelected ? [] : optionsItems)
        } else {
            setSelectedItems(values ?? [])
        }
    }

    const isFormValid = () => (
        isValid(siteId)
        && isValid(periodFrom)
        && isValid(periodTo)
        && isValid(calculationBasis)
    )

    const runMrp = async (e) => {
        if (e) e.preventDefault()
        setIsLoading(true)

        const formData = {
            site_id: siteId,
            period_from: dayjs(periodFrom).format('YYYY-MM-DD'),
            period_to: dayjs(periodTo).format('YYYY-MM-DD'),
            calculation_basis: calculationBasis,
            pallet_type: palletTypes,
            item_id: selectedItems.map((item) => item.id)
        }

        await axios
            .post('/api/run-material-requirements-planning', formData, config)
            .then(({ data }) => {
                setMrpResult(data?.success?.data ?? null)
                setLastRunAt(dayjs())
                Swal.fire({
                    icon: "success",
                    customClass: 'success',
                    showCloseButton: true,
                    iconColor: '#00B78E',
                    text: data?.success?.message
                })
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response, showDescription: true })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const openProductionDetails = (row) => {
        setSelectedRow(row)
        setProductionDetailsOpen(true)
    }

    const openMaterialDetails = (row) => {
        setSelectedRow(row)
        setMaterialDetailsOpen(true)
    }

    const closeDetails = () => {
        setProductionDetailsOpen(false)
        setMaterialDetailsOpen(false)
        setSelectedRow(null)
    }

    /* Navigates to the Production Order board with this MRP row's item/qty pre-filled -
       takes priority over whatever a customer selection there would otherwise populate.
       Matched by item_no/material_no (not MRP's own row id, which does not correspond to the
       real Item id). */
    const createProductionOrderFromMrp = (row) => {
        navigate('/production-order-board', {
            state: {
                prefill: {
                    itemNo: row.item_no ?? row.material_no,
                    qtyToProduce: row.additional_requirement
                }
            }
        })
    }

    /* Navigates to the Purchase Order page (always the standard COLLECTION type for an MRP-driven
       order) with this MRP row's item/qty pre-filled - matched by item_no/material_no. */
    const createPurchaseOrderFromMrp = (row) => {
        navigate('/purchase-order', {
            state: {
                prefill: {
                    itemNo: row.item_no ?? row.material_no,
                    qty: row.additional_requirement
                }
            }
        })
    }

    /* BOM scrap allowance - prefer the backend-computed values, fall back to computing them
       client-side (Base Required Qty x Scrap %) if a row is ever missing them */
    const getMaterialScrapQuantity = (row) => {
        if (isValid(row.scrap_quantity)) return row.scrap_quantity
        const base = Number(row.base_required_quantity) || 0
        const percent = Number(row.scrap_percent) || 0
        return Math.round(base * (percent / 100) * 100) / 100
    }

    const getMaterialRequiredQuantity = (row) => {
        if (isValid(row.required_quantity)) return row.required_quantity
        const base = Number(row.base_required_quantity) || 0
        return Math.round((base + getMaterialScrapQuantity(row)) * 100) / 100
    }

    const optionsSites = sites?.map(option => ({ label: option.name, id: option.id }))
    const optionsItems = items?.map(option => ({ label: option.item_no, id: option.id }))
    const optionsItemsWithAll = [{ id: 'all', label: t('all') }, ...optionsItems]
    const optionsPalletTypesWithAll = [{ id: 'all', name: t('all') }, ...(Array.isArray(palletTypeOptions) ? palletTypeOptions : [])]
    const isAllPalletTypesSelected = palletTypeOptions.length > 0 && palletTypes.length === palletTypeOptions.length
    const isAllItemsSelected = optionsItems.length > 0 && selectedItems.length === optionsItems.length

    const actionButtonStyle = { color: '#1976D2', fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '14px', lineHeight: '100%', letterSpacing: '0.15px' }

    const productionColumns = [
        {
            field: 'item_no',
            headerName: t('item'),
            flex: 0.7,
            minWidth: 110,
            renderCell: (params) => (
                <div className='flex items-center gap-2'>
                    <i className="fa-solid fa-eye text-[#1976D2] text-[13px]"></i>
                    <span>{params.value}</span>
                </div>
            )
        },
        { field: 'description', headerName: t('description'), flex: 1.2, minWidth: 170 },
        { field: 'calculated_demand', headerName: t('calculated_demand'), flex: 0.9, minWidth: 140, valueFormatter: ({ value }) => value?.toLocaleString() },
        { field: 'firm_sales_orders', headerName: t('firm_sales_orders'), flex: 0.9, minWidth: 140, valueFormatter: ({ value }) => value?.toLocaleString() },
        { field: 'mrp_demand', headerName: t('mrp_demand'), flex: 0.8, minWidth: 120, valueFormatter: ({ value }) => value?.toLocaleString() },
        { field: 'available_inventory', headerName: t('available_inventory'), flex: 0.9, minWidth: 140, valueFormatter: ({ value }) => value?.toLocaleString() },
        { field: 'production_orders_in_progress', headerName: t('incoming_supply'), flex: 0.8, minWidth: 130, valueFormatter: ({ value }) => value?.toLocaleString() },
        { field: 'additional_requirement', headerName: t('additional_requirement'), flex: 0.9, minWidth: 150, valueFormatter: ({ value }) => value?.toLocaleString() },
        {
            field: 'supply_method',
            headerName: t('supply_method'),
            flex: 0.7,
            minWidth: 120,
            renderCell: (params) => <SupplyMethodChip method={params.value} t={t} />
        },
        {
            field: 'actions',
            headerName: t('actions'),
            sortable: false,
            flex: 0.8,
            minWidth: 170,
            renderCell: (params) => (
                <Tooltip disableInteractive title={t('create_new_x', { x: (params.row.supply_method === 'buy' ? t('purchase_order_action') : t('production_order_action')).toLowerCase() })} placement='bottom'>
                    <button
                        type='button'
                        onClick={(e) => {
                            e.stopPropagation()
                            if (params.row.supply_method === 'buy') {
                                createPurchaseOrderFromMrp(params.row)
                            } else {
                                createProductionOrderFromMrp(params.row)
                            }
                        }}
                        className='flex items-center gap-1'
                        style={actionButtonStyle}
                    >
                        <i className="fa-solid fa-plus" style={{ fontSize: '10px' }}></i>
                        {params.row.supply_method === 'buy' ? t('purchase_order_action') : t('production_order_action')}
                    </button>
                </Tooltip>
            )
        }
    ]

    const materialColumns = [
        {
            field: 'material_no',
            headerName: t('mrp_material'),
            flex: 0.7,
            minWidth: 110,
            renderCell: (params) => (
                <div className='flex items-center gap-2'>
                    <i className="fa-solid fa-eye text-[#1976D2] text-[13px]"></i>
                    <span>{params.value}</span>
                </div>
            )
        },
        { field: 'description', headerName: t('description'), flex: 1.2, minWidth: 170 },
        { field: 'base_required_quantity', headerName: t('base_required_qty'), flex: 0.9, minWidth: 150, valueFormatter: ({ value }) => value?.toLocaleString() },
        {
            field: 'scrap_percent',
            headerName: t('scrap_percent_header'),
            flex: 0.6,
            minWidth: 100,
            sortable: false,
            renderCell: (params) => `${params.value ?? 0}%`
        },
        {
            field: 'required_quantity',
            headerName: t('required_qty'),
            flex: 0.9,
            minWidth: 140,
            sortable: false,
            renderCell: (params) => getMaterialRequiredQuantity(params.row)?.toLocaleString()
        },
        { field: 'available_inventory', headerName: t('available_inventory'), flex: 0.9, minWidth: 140, valueFormatter: ({ value }) => value?.toLocaleString() },
        { field: 'incoming_po', headerName: t('incoming_supply'), flex: 0.8, minWidth: 130, valueFormatter: ({ value }) => value?.toLocaleString() },
        { field: 'additional_requirement', headerName: t('additional_requirement'), flex: 0.9, minWidth: 150, valueFormatter: ({ value }) => value?.toLocaleString() },
        {
            field: 'supply_method',
            headerName: t('supply_method'),
            flex: 0.7,
            minWidth: 120,
            renderCell: (params) => <SupplyMethodChip method={params.value} t={t} />
        },
        {
            field: 'actions',
            headerName: t('actions'),
            sortable: false,
            flex: 0.8,
            minWidth: 170,
            renderCell: (params) => (
                <Tooltip disableInteractive title={t('create_new_x', { x: (params.row.supply_method === 'buy' ? t('purchase_order_action') : t('production_order_action')).toLowerCase() })} placement='bottom'>
                    <button
                        type='button'
                        onClick={(e) => {
                            e.stopPropagation()
                            if (params.row.supply_method === 'buy') {
                                createPurchaseOrderFromMrp(params.row)
                            } else {
                                createProductionOrderFromMrp(params.row)
                            }
                        }}
                        className='flex items-center gap-1'
                        style={actionButtonStyle}
                    >
                        <i className="fa-solid fa-plus" style={{ fontSize: '14px' }}></i>
                        {params.row.supply_method === 'buy' ? t('purchase_order_action') : t('production_order_action')}
                    </button>
                </Tooltip>
            )
        }
    ]

    const dataGridSx = {
        '& .MuiDataGrid-columnHeaderTitle': { color: '#88909C !important', fontWeight: '400' },
        '& .MuiDataGrid-row': { cursor: 'pointer' },
        '& .MuiDataGrid-row:hover': { backgroundColor: '#FAFAFA' },
        '& .MuiDataGrid-cell': { borderBottom: '1px solid #E5E7EB' },
        border: 'none',
        background: 'white'
    }

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <AppLayout>
                <div className='p-6 flex flex-col w-full gap-5'>
                    {/* Planning Parameters */}
                    <div className='w-full bg-white rounded-md'>
                        <div className='p-5 flex justify-between items-center border-b border-[#E5E7EB]'>
                            <p style={{ fontWeight: 600, fontSize: '16px' }}>{t('mrp')} - {t('planning_parameters')}</p>
                        </div>

                        <div className='p-5'>
                            <div className='pb-4'>
                                <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('planning_parameters')}</p>
                            </div>

                            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                                {/* Site */}
                                <div className='w-full'>
                                    <p className="text-[14px] text-[#718096]">{t('site')} *</p>
                                    <Autocomplete
                                        disablePortal
                                        disableClearable
                                        id="mrp-site-autocomplete"
                                        options={optionsSites}
                                        value={siteId ? { label: siteName, id: siteId } : null}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        onChange={handleSiteChange}
                                        sx={{
                                            width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                            boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                        }}
                                        renderInput={(params) => <TextField {...params} />}
                                    />
                                </div>

                                {/* Period From */}
                                <div className='w-full'>
                                    <p className="text-[14px] text-[#718096]">{t('period_from')} *</p>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <MobileDatePicker
                                            sx={{ ...TEXTFIELD_STYLE, width: '100%' }}
                                            format="YYYY-MM-DD"
                                            value={periodFrom}
                                            onChange={(newValue) => {
                                                setPeriodFrom(newValue)
                                                if (newValue && periodTo && dayjs(periodTo).isBefore(dayjs(newValue), 'day')) {
                                                    setPeriodTo(null)
                                                }
                                            }}
                                        />
                                    </LocalizationProvider>
                                </div>

                                {/* Period To */}
                                <div className='w-full'>
                                    <p className="text-[14px] text-[#718096]">{t('period_to')} *</p>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <MobileDatePicker
                                            sx={{ ...TEXTFIELD_STYLE, width: '100%' }}
                                            format="YYYY-MM-DD"
                                            value={periodTo}
                                            minDate={periodFrom || undefined}
                                            onChange={(newValue) => setPeriodTo(newValue)}
                                        />
                                    </LocalizationProvider>
                                </div>

                                {/* Calculation Basis */}
                                <div className='w-full'>
                                    <p className="text-[14px] text-[#718096]">{t('calculation_basis')} *</p>
                                    <FormControl sx={{ width: '100%' }}>
                                        <Select
                                            displayEmpty
                                            value={calculationBasis}
                                            onChange={(e) => setCalculationBasis(e.target.value)}
                                            sx={TEXTFIELD_STYLE}
                                        >
                                            <MenuItem value="" disabled>{t('select_calculation_basis')}</MenuItem>
                                            {calculationBasisOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>

                                {/* Pallet Type - optional, multiselect */}
                                <div className='w-full'>
                                    <p className="text-[14px] text-[#718096]">{t('pallet_type')}</p>
                                    <FormControl sx={{ width: '100%' }}>
                                        <Select
                                            multiple
                                            displayEmpty
                                            value={palletTypes}
                                            onChange={handlePalletTypesChange}
                                            renderValue={(selected) => selected.length === 0
                                                ? t('all')
                                                : selected.map((value) => palletTypeOptions.find((option) => option.id === value)?.name ?? value).join(', ')
                                            }
                                            sx={TEXTFIELD_STYLE}
                                        >
                                            {optionsPalletTypesWithAll.map((option) => (
                                                <MenuItem key={option.id} value={option.id}>
                                                    <Checkbox checked={option.id === 'all' ? isAllPalletTypesSelected : palletTypes.indexOf(option.id) > -1} />
                                                    <ListItemText primary={option.name} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>

                                {/* Item - optional, multiselect */}
                                <div className='w-full'>
                                    <p className="text-[14px] text-[#718096]">{t('item')}</p>
                                    <Autocomplete
                                        multiple
                                        disablePortal
                                        disableCloseOnSelect
                                        id="mrp-item-autocomplete"
                                        options={optionsItemsWithAll}
                                        value={selectedItems}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        onChange={handleItemsChange}
                                        renderOption={(props, option) => (
                                            <li {...props} key={`mrp-item-option-${option.id}`}>
                                                <Checkbox checked={option.id === 'all' ? isAllItemsSelected : selectedItems.some((item) => item.id === option.id)} />
                                                <ListItemText primary={option.label} />
                                            </li>
                                        )}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip size='small' label={option.label} {...getTagProps({ index })} />
                                            ))
                                        }
                                        sx={{
                                            width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-root': { padding: '2px 8px !important' },
                                            boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                        }}
                                        renderInput={(params) => <TextField {...params} />}
                                    />
                                </div>
                            </div>

                            <div className='w-full flex justify-end pt-4'>
                                <button
                                    type="button"
                                    disabled={!isFormValid()}
                                    onClick={runMrp}
                                    className='w-full md:w-auto text-white font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#DBA34B] hover:bg-[#CF7300] disabled:bg-[#B8B7BC]'
                                >
                                    <span className='pr-2'><i className="fa-solid fa-play"></i></span> {t('run_mrp')}
                                </button>
                            </div>
                        </div>
                    </div>

                    {mrpResult ? (
                        <>
                            {/* MRP Results - Overview */}
                            <div className='w-full bg-white rounded-md'>
                                <div className='p-5 flex justify-between items-center border-b border-[#E5E7EB]'>
                                    <div>
                                        <p style={{ fontWeight: 600, fontSize: '16px' }}>{t('mrp_results_overview')}</p>
                                        <p className='text-[12px] text-[#88909C] pt-1'>
                                            {t('last_run')}: {lastRunAt ? lastRunAt.format('YYYY-MM-DD, h:mm:ss A') : '-'} &nbsp;•&nbsp; {t('site')}: {mrpResult.site?.name} &nbsp;•&nbsp; {t('period')}: {mrpResult.period_from} - {mrpResult.period_to}
                                        </p>
                                    </div>
                                    <button
                                        type='button'
                                        className='flex items-center gap-1 flex-shrink-0'
                                        style={{ color: '#1976D2', fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '14px', lineHeight: '17.5px', letterSpacing: '0.4px' }}
                                    >
                                        <i className="fa-solid fa-download"></i> {t('export_mrp_results')}
                                    </button>
                                </div>
                                <div className='p-5 grid grid-cols-1 md:grid-cols-4 gap-4'>
                                    <KpiCard icon='fa-chart-simple' label={t('calculated_demand')} value={`${mrpResult.overview?.calculated_demand?.toLocaleString()} ${t('pallets')}`} />
                                    <KpiCard icon='fa-file-lines' label={t('firm_sales_orders')} value={`${mrpResult.overview?.firm_sales_orders?.toLocaleString()} ${t('pallets')}`} />
                                    <KpiCard icon='fa-gear' label={t('finished_product_requirements')} value={`${mrpResult.overview?.items_requiring_production ?? 0} ${t('items')}`} />
                                    <KpiCard icon='fa-cubes-stacked' label={t('materials_requiring_purchase')} value={`${mrpResult.overview?.materials_requiring_purchase} ${t('materials')}`} />
                                </div>
                            </div>

                            {/* Production Requirements for Finished Products */}
                            <div className='w-full bg-white rounded-md'>
                                <div className='p-5 flex justify-between items-center border-b border-[#E5E7EB]'>
                                    <p style={{ fontWeight: 600, fontSize: '16px' }}>{t('production_requirements_for_finished_products')}</p>

                                </div>
                                <div className=''>
                                    {(mrpResult.production_requirements ?? []).length > 0 ? (
                                        <DataGrid
                                            autoHeight
                                            headerHeight={64}
                                            disableColumnMenu
                                            disableSelectionOnClick
                                            rows={mrpResult.production_requirements ?? []}
                                            columns={productionColumns}
                                            sx={dataGridSx}
                                            pageSize={productionPageSize}
                                            onPageSizeChange={(newPageSize) => setProductionPageSize(newPageSize)}
                                            rowsPerPageOptions={[10, 25, 50]}
                                            onRowClick={(params) => openProductionDetails(params.row)}
                                        />
                                    ) : (
                                        <EmptyState
                                            icon='fa-circle-check'
                                            title={t('no_production_requirements_title')}
                                            description={t('no_production_requirements_description')}
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Material Requirements */}
                            <div className='w-full bg-white rounded-md'>
                                <div className='p-5 flex justify-between items-center border-b border-[#E5E7EB]'>
                                    <p style={{ fontWeight: 600, fontSize: '16px' }}>{t('material_requirements')}</p>

                                </div>
                                <div className=''>
                                    {(mrpResult.material_requirements ?? []).length > 0 ? (
                                        <DataGrid
                                            autoHeight
                                            headerHeight={64}
                                            disableColumnMenu
                                            disableSelectionOnClick
                                            rows={mrpResult.material_requirements ?? []}
                                            columns={materialColumns}
                                            sx={dataGridSx}
                                            pageSize={materialPageSize}
                                            onPageSizeChange={(newPageSize) => setMaterialPageSize(newPageSize)}
                                            rowsPerPageOptions={[10, 25, 50]}
                                            onRowClick={(params) => openMaterialDetails(params.row)}
                                        />
                                    ) : (
                                        <EmptyState
                                            icon='fa-boxes-stacked'
                                            title={t('no_material_requirements_title')}
                                            description={t('no_material_requirements_description')}
                                        />
                                    )}
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>
            </AppLayout>

            {/* Production Requirement Details modal */}
            <Modal open={productionDetailsOpen} onClose={closeDetails}>
                <Box sx={detailsModalStyle}>
                    <div className='flex justify-between items-center p-5 w-full' style={{ backgroundColor: '#336195', borderRadius: '5px 5px 0 0' }}>
                        <div className='flex items-baseline justify-between w-full'>
                            <p className='text-[18px] font-[600] text-white'>{selectedRow?.item_no} - {selectedRow?.description} {t('requirement_details')}</p>
                            <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-white">
                                <button onClick={closeDetails}><i className="fa-solid fa-plus"></i></button>
                            </div>
                        </div>
                    </div>

                    <div className='p-5 flex gap-5 overflow-x-auto'>
                        <div className='border border-[#E5E7EB] rounded-md overflow-hidden h-fit divide-y divide-[#E5E7EB] flex-shrink-0' style={{ width: '400px' }}>
                            <p style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: '13px', background: '#F7F9FB', color: '#667B96' }} className='uppercase px-2.5 pt-[15px] pb-[15px]'>{t('demand_calculation')}</p>
                            <DetailField label={t('historical_period')} value={selectedRow?.demand_calculation?.historical_period} />
                            <DetailField label={t('historical_shipped_quantity')} value={`${selectedRow?.demand_calculation?.historical_shipped_qty?.toLocaleString()} ${t('pallets')}`} />
                            <DetailField label={t('average_monthly_demand')} value={`${selectedRow?.demand_calculation?.average_monthly_demand?.toLocaleString()} ${t('pallets')}`} />
                            <DetailField label={t('calculated_demand_for_planning_period')} value={`${selectedRow?.demand_calculation?.calculated_demand_for_period?.toLocaleString()} ${t('pallets')}`} />
                            <DetailField label={t('firm_sales_orders_within_period')} value={`${selectedRow?.demand_calculation?.firm_sales_orders_within_period?.toLocaleString()} ${t('pallets')}`} />
                            <DetailField label={t('mrp_demand_used')} value={`${selectedRow?.demand_calculation?.mrp_demand_used?.toLocaleString()} ${t('pallets')}`} highlight />
                        </div>

                        <div className='border border-[#E5E7EB] rounded-md overflow-hidden h-fit divide-y divide-[#E5E7EB] flex-shrink-0' style={{ width: '400px' }}>
                            <p style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: '13px', background: '#F7F9FB', color: '#667B96' }} className='uppercase px-2.5 pt-[15px] pb-[15px]'>{t('supply_and_requirement')}</p>
                            <DetailField label={t('supply_method')} value={<SupplyMethodChip method={selectedRow?.supply_method} t={t} />} />
                            <DetailField label={t('mrp_demand_used')} value={`${selectedRow?.mrp_demand?.toLocaleString()} ${t('pallets')}`} />
                            <DetailField label={t('available_inventory_on_hand')} value={`(${selectedRow?.available_inventory?.toLocaleString()}) ${t('pallets')}`} />
                            <DetailField label={t('production_orders_within_period')} value={`(${selectedRow?.production_orders_in_progress?.toLocaleString()}) ${t('pallets')}`} />
                            <DetailField label={t('additional_production_required')} value={`${selectedRow?.additional_production_required?.toLocaleString()} ${t('pallets')}`} highlight />
                        </div>
                    </div>
                </Box>
            </Modal>

            {/* Material Requirement Details modal */}
            <Modal open={materialDetailsOpen} onClose={closeDetails}>
                <Box sx={detailsModalStyle}>
                    <div className='flex justify-between items-center p-5 w-full' style={{ backgroundColor: '#336195', borderRadius: '5px 5px 0 0' }}>
                        <div className='flex items-baseline justify-between w-full'>
                            <p className='text-[18px] font-[600] text-white'>{selectedRow?.material_no} - {selectedRow?.description} {t('material_requirement_details')}</p>
                            <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-white">
                                <button onClick={closeDetails}><i className="fa-solid fa-plus"></i></button>
                            </div>
                        </div>
                    </div>

                    <div className='p-5'>
                        <div className='flex gap-5 pb-5 overflow-x-auto'>
                            <div className='border border-[#E5E7EB] rounded-md overflow-hidden h-fit divide-y divide-[#E5E7EB] flex-shrink-0' style={{ width: '310px' }}>
                                <p style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: '13px', background: '#F7F9FB', color: '#667B96' }} className='uppercase px-2.5 pt-[15px] pb-[15px]'>{t('bom_information')}</p>
                                <DetailField label={t('mrp_material')} value={selectedRow?.material_no} />
                                <DetailField label={t('description')} value={selectedRow?.description} />
                                <DetailField label={t('quantity_per_finished_item')} value={selectedRow?.bom_information?.quantity_per_pallet} />
                                <DetailField label={t('unit_of_measure')} value={selectedRow?.bom_information?.unit_of_measure} />
                            </div>

                            <div className='border border-[#E5E7EB] rounded-md overflow-hidden h-fit divide-y divide-[#E5E7EB] flex-shrink-0' style={{ width: '310px' }}>
                                <p style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: '13px', background: '#F7F9FB', color: '#667B96' }} className='uppercase px-2.5 pt-[15px] pb-[15px]'>{t('requirement_calculation')}</p>
                                <DetailField label={t('additional_requirement')} value={`${selectedRow?.additional_requirement?.toLocaleString()} ${selectedRow?.bom_information?.unit_of_measure ?? t('pieces')}`} />
                                <DetailField label={t('quantity_per_finished_item')} value={selectedRow?.bom_information?.quantity_per_pallet} />
                                <DetailField label={t('bom_requirement')} value={`${selectedRow?.base_required_quantity?.toLocaleString()} ${selectedRow?.bom_information?.unit_of_measure ?? t('pieces')}`} />
                                <DetailField label={t('scrap_allowance', { percent: selectedRow?.scrap_percent ?? 0 })} value={`+${getMaterialScrapQuantity(selectedRow ?? {})?.toLocaleString()} ${selectedRow?.bom_information?.unit_of_measure ?? t('pieces')}`} />
                                <DetailField label={t('total_required_quantity')} value={`${getMaterialRequiredQuantity(selectedRow ?? {})?.toLocaleString()} ${selectedRow?.bom_information?.unit_of_measure ?? t('pieces')}`} highlight />
                            </div>

                            <div className='border border-[#E5E7EB] rounded-md overflow-hidden h-fit divide-y divide-[#E5E7EB] flex-shrink-0' style={{ width: '310px' }}>
                                <p style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: '13px', background: '#F7F9FB', color: '#667B96' }} className='uppercase px-2.5 pt-[15px] pb-[15px]'>{t('supply_and_requirement')}</p>
                                <DetailField label={t('total_required_quantity')} value={`${getMaterialRequiredQuantity(selectedRow ?? {})?.toLocaleString()} ${selectedRow?.bom_information?.unit_of_measure ?? t('pieces')}`} />
                                <DetailField label={t('available_inventory_on_hand')} value={`(${selectedRow?.available_inventory?.toLocaleString()}) ${selectedRow?.bom_information?.unit_of_measure ?? t('pieces')}`} />
                                <DetailField label={t('incoming_supply_within_period')} value={`(${selectedRow?.incoming_po?.toLocaleString()}) ${selectedRow?.bom_information?.unit_of_measure ?? t('pieces')}`} />
                                <DetailField label={t('additional_purchase_required')} value={`${selectedRow?.additional_purchase_required?.toLocaleString()} ${selectedRow?.bom_information?.unit_of_measure ?? t('pieces')}`} highlight />
                            </div>
                        </div>

                        {/* Incoming Purchase Orders table - not needed for now, kept commented out
                        <p className='text-[13px] font-[600] uppercase text-[#A1ACB8] pb-2'>{t('incoming_purchase_orders_header')}</p>
                        <div className='border rounded-md overflow-hidden'>
                            <div className='grid grid-cols-6 gap-2 px-4 py-2 text-[12px] font-[600] text-[#88909C] border-b bg-[#FAFAFA]'>
                                <span>{t('po_number')}</span>
                                <span>{t('vendor')}</span>
                                <span>{t('expected_receipt_date')}</span>
                                <span>{t('quantity')}</span>
                                <span>{t('unit_of_measure')}</span>
                                <span>{t('status')}</span>
                            </div>
                            {(selectedRow?.incoming_purchase_orders ?? []).map((po, idx) => (
                                <div key={idx} className={`grid grid-cols-6 gap-2 px-4 py-2.5 text-[13px] ${idx > 0 ? 'border-t' : ''}`}>
                                    <span className='font-[500]'>{po.po_number}</span>
                                    <span>{po.vendor}</span>
                                    <span>{po.expected_receipt_date}</span>
                                    <span>{po.quantity?.toLocaleString()}</span>
                                    <span>{po.unit_of_measure}</span>
                                    <span>
                                        <Chip label={po.status} size='small' sx={{ backgroundColor: '#EAF2FB', color: '#336195', fontWeight: 600 }} />
                                    </span>
                                </div>
                            ))}
                        </div>
                        */}
                    </div>
                </Box>
            </Modal>
        </>
    )
}

export default Mrp
